import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    OnInit,
    inject,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { IChecklistV2ForList } from '@fi-shared/checklist/checklist-v2';
import { ObjectTypes, SideListOptionType } from '@fi-shared/types';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import {
    IItemsListAction2,
    IItemsListDataFetchResult,
    ISideListOptionsItem,
} from '@fi-shared/fi-items-list/fi-items-list';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { FiChecklist2AuditService } from '@fi-shared/checklist/fi-checklist2-audit/fi-checklist2-audit.service';
import { FiChecklist2AuditListTablePerUnitComponent } from '@fi-shared/checklist/fi-checklist2-audit/fi-checklist2-audit-list/fi-checklist2-audit-list-table-per-unit/fi-checklist2-audit-list-table-per-unit.component';
import { ILocalSearchParameter } from '@fi-shared/models/models';
import { FiChecklist2ListBase } from '@fi-shared/checklist/fi-checklist2-list/fi-checklist2-list-base';

@Component({
    selector: 'fi-checklist2-audit-list',
    templateUrl: './fi-checklist2-audit-list.component.html',
    styleUrls: ['./fi-checklist2-audit-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-checklist2-audit-list' },
    standalone: false,
})
export class FiChecklist2AuditListComponent extends FiChecklist2ListBase implements OnInit {
    private fiChecklist2AuditService = inject(FiChecklist2AuditService);

    protected sideListOptions: ISideListOptionsItem[];
    //defaultSortColumn = 'DueDateTime';
    public readonly defaultSortColumnName = 'Name';
    public readonly defaultSortDirection = false;
    public readonly objectType = ObjectTypes.CheckList;
    public readonly itemsListType = FiItemsListType.ChecklistV2Audits;
    public actions: IItemsListAction2<IChecklistV2ForList>[];

    public readonly auditMode: boolean = true;

    @ViewChild(FiChecklist2AuditListTablePerUnitComponent)
    protected auditListTablePerUnitComponent: FiChecklist2AuditListTablePerUnitComponent;

    constructor(injector: Injector) {
        super(injector);
    }

    public ngOnInit(): void {
        this.initSideList();
        this.actions = this.getChecklistAuditListActions();
    }

    public loadListDataFn(loadArgs: ILoadDataArgs): Promise<IItemsListDataFetchResult<IChecklistV2ForList>> {
        const parameter = this.getItemsListParameter(loadArgs);
        parameter.AllUnitsMode = false;
        parameter.AuditMode = true;
        return this.fiChecklist2AuditService.getChecklistAuditListData(parameter);
    }

    protected isTablePerUnitHidden(): boolean {
        return this.filterType === SideListOptionType.Expired || this.extendedSearchMode;
    }

    private initSideList(): void {
        this.sideListOptions = [
            { value: SideListOptionType.SharedWithMe, text: this.appTextService.get('common.shared_with_me') },
        ];

        // "My checklists" and "Expired" are not allowed for "read" users
        if (this.fiChecklist2AuditService.isChecklistAuditShareRights()) {
            this.sideListOptions.push(
                ...[
                    {
                        value: SideListOptionType.CreatedBy,
                        text: this.appTextService.get('common.created_by_me'),
                    },
                    {
                        value: SideListOptionType.Expired,
                        text: this.appTextService.get('checklist.expired_checklists'),
                    },
                ],
            );
        }
    }

    private getChecklistAuditListActions(): IItemsListAction2<IChecklistV2ForList>[] {
        const actions: IItemsListAction2<IChecklistV2ForList>[] = [
            this.fiChecklistActionsHelperService.getChecklistEditAction<IChecklistV2ForList>({
                checklistIdFn: (data) => data.Id,
                auditMode: this.auditMode,
                templateMode: false,
            }),
            this.fiChecklistActionsHelperService.getChecklistGenerateTemplateAction({
                checklistIdFn: (data) => data.Id,
                auditMode: this.auditMode,
            }),
            this.fiChecklistActionsHelperService.getChecklistGenerateAuditChecklistAction({
                checklistIdFn: (data) => data.Id,
                auditMode: this.auditMode,
            }),
            this.fiChecklistActionsHelperService.getDeleteChecklistResponsesAction<IChecklistV2ForList>({
                getChecklistToScheduleIdFn: (data) => data.ChecklistToScheduleId,
                getChecklistNameFn: (data) => data.Name,
                getScheduleNameFn: (data) => data.ScheduleName,
                disabled: (data) => !data.HasAnswers,
                afterDeleteFn: (data) => {
                    this.reloadData();
                    this.showResultSnackbar(this.appTextService.get('checklist.responses_deleted'));
                },
                businessUnitId: this.userDataService.getUserBusinessUnitId(),
                auditMode: this.auditMode,
            }),
            this.fiChecklistActionsHelperService.getScheduleChecklistAction({
                auditMode: this.auditMode,
                checklistIdFn: (data) => data.Id,
                templateMode: false,
            }),
            this.fiChecklistActionsHelperService.getDeleteChecklistAction<IChecklistV2ForList>({
                onClick: (data) => {
                    this.fiChecklistHelperService
                        .getConfirmDeleteChecklistDialog({
                            item: data,
                            checklistIdFn: () => data.Id,
                            auditMode: this.auditMode,
                        })
                        .then((result) => {
                            if (result.result) {
                                this.fiChecklist2Service.deleteChecklists([data.Id], this.auditMode).then(() => {
                                    const objectType = !this.auditMode
                                        ? ObjectTypes.Checklist2
                                        : ObjectTypes.Checklist2Audit;
                                    this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(
                                        objectType,
                                        [data.Id],
                                        data.Name,
                                    );

                                    this.removeData([data]); // for excluding deleted checklist from list
                                });
                            }
                        });
                },
                auditMode: this.auditMode,
                templateMode: false,
            }),
        ];
        return actions;
    }

    public onSideListOptionsChanged(filterType: number): void {
        super.onSideListOptionsChanged(filterType);
        if (this.auditListTablePerUnitComponent) {
            this.auditListTablePerUnitComponent.onSideListOptionsChanged(filterType, { skipUpdateStateParams: true });
        }
    }

    public onSearchChanged($event: ILocalSearchParameter): void {
        super.onSearchChanged($event);
        if (this.auditListTablePerUnitComponent) {
            this.auditListTablePerUnitComponent.onSearchChanged($event);
        }
    }

    protected showOptionsColumn(): boolean {
        return !this.extendedSearchMode;
    }
}
